/**
 * Shared modal open/close for `[data-modal]` dialogs and `[data-modal-trigger]`.
 *
 * @return {void}
 */
import { applyAppointmentCatalogSelects } from './appointment-form-prefill';

export function initModalTriggers() {
	const modals = Array.from(document.querySelectorAll('[data-modal]'));

	if (0 === modals.length) {
		return;
	}

	const modalById = new Map(
		modals.map((modal) => [modal.getAttribute('data-modal'), modal])
	);

	/**
	 * @return {number}
	 */
	const getScrollbarWidth = () =>
		window.innerWidth - document.documentElement.clientWidth;

	/**
	 * @return {void}
	 */
	const lockBodyScroll = () => {
		const scrollbarWidth = getScrollbarWidth();

		document.body.classList.add('site-modal-open');

		if (0 < scrollbarWidth) {
			document.body.style.paddingRight = `${scrollbarWidth}px`;
		}
	};

	/**
	 * @return {void}
	 */
	const unlockBodyScroll = () => {
		document.body.classList.remove('site-modal-open');
		document.body.style.paddingRight = '';
	};

	/**
	 * @return {void}
	 */
	const closeAppointmentDatePickers = () => {
		document
			.querySelectorAll('input[name="appointment-date"]')
			.forEach((input) => {
				input._flatpickr?.close();
			});
	};

	/**
	 * @param {HTMLElement|null} modal
	 * @param {string|null|undefined} specialistId
	 * @param {string|null|undefined} serviceId
	 * @return {void}
	 */
	const dispatchModalOpen = (modal, specialistId, serviceId) => {
		if (!modal) {
			return;
		}

		const modalId = modal.getAttribute('data-modal') || '';

		document.dispatchEvent(
			new CustomEvent('lecardo-vein:modal:open', {
				bubbles: true,
				detail: {
					modalId,
					specialistId: specialistId || null,
					serviceId: serviceId || null,
				},
			})
		);
	};

	/**
	 * @param {HTMLElement|null} modal
	 * @param {string|null|undefined} specialistId
	 * @param {string|null|undefined} serviceId
	 * @return {void}
	 */
	const openModal = (modal, specialistId, serviceId) => {
		if (!modal) {
			return;
		}

		const hadOpenModal = Boolean(
			document.querySelector('[data-modal].is-open')
		);

		if (specialistId) {
			modal.dataset.specialistId = specialistId;
		} else {
			delete modal.dataset.specialistId;
		}

		if (serviceId) {
			modal.dataset.serviceId = serviceId;
		} else {
			delete modal.dataset.serviceId;
		}

		modal.hidden = false;
		modal.classList.add('is-open');
		modal.setAttribute('aria-hidden', 'false');

		if (!hadOpenModal) {
			lockBodyScroll();
		}

		window.requestAnimationFrame(() => {
			modal.querySelector('[data-modal-dialog]')?.focus();
		});

		if ('appointment' === modal.getAttribute('data-modal')) {
			applyAppointmentCatalogSelects(modal, {
				specialistId: specialistId || null,
				serviceId: serviceId || null,
			});
		}

		dispatchModalOpen(modal, specialistId, serviceId);
	};

	/**
	 * @param {HTMLElement|null} modal
	 * @return {void}
	 */
	const closeModal = (modal) => {
		if (!modal) {
			return;
		}

		closeAppointmentDatePickers();

		modal.classList.remove('is-open');
		modal.hidden = true;
		modal.setAttribute('aria-hidden', 'true');

		if (!document.querySelector('[data-modal].is-open')) {
			unlockBodyScroll();
		}
	};

	/**
	 * @param {HTMLElement|null} fromModal
	 * @param {HTMLElement|null} toModal
	 * @return {void}
	 */
	const swapModal = (fromModal, toModal) => {
		closeAppointmentDatePickers();

		if (fromModal) {
			fromModal.classList.remove('is-open');
			fromModal.hidden = true;
			fromModal.setAttribute('aria-hidden', 'true');
		}

		if (!toModal) {
			if (!document.querySelector('[data-modal].is-open')) {
				unlockBodyScroll();
			}

			return;
		}

		const hadOpenModal = Boolean(
			document.querySelector('[data-modal].is-open')
		);

		toModal.hidden = false;
		toModal.classList.add('is-open');
		toModal.setAttribute('aria-hidden', 'false');

		if (!hadOpenModal) {
			lockBodyScroll();
		}

		window.requestAnimationFrame(() => {
			toModal.querySelector('[data-modal-dialog]')?.focus();
		});

		dispatchModalOpen(toModal);
	};

	document.querySelectorAll('[data-modal-trigger]').forEach((trigger) => {
		trigger.addEventListener('click', (event) => {
			if (trigger instanceof HTMLAnchorElement) {
				event.preventDefault();
			}

			const modalId = trigger.getAttribute('data-modal-trigger');
			const modal = modalById.get(modalId) || null;
			const specialistId = trigger.getAttribute('data-specialist-id');
			const serviceId = trigger.getAttribute('data-service-id');

			openModal(modal, specialistId, serviceId);
		});
	});

	document.addEventListener('click', (event) => {
		if (!(event.target instanceof Element)) {
			return;
		}

		const menu_item = event.target.closest('li.js-open-question-modal');
		const link = menu_item?.querySelector(':scope > a');

		if (!menu_item || !link || !link.contains(event.target)) {
			return;
		}

		event.preventDefault();

		openModal(modalById.get('question') || null);
	});

	document.addEventListener('lecardo-vein:modal:open-request', (event) => {
		const modalId = event.detail?.modalId;
		const modal = modalById.get(modalId) || null;
		const specialistId = event.detail?.specialistId || null;
		const serviceId = event.detail?.serviceId || null;

		openModal(modal, specialistId, serviceId);
	});

	document.addEventListener('lecardo-vein:modal:close-request', (event) => {
		const modalId = event.detail?.modalId;
		const modal = modalById.get(modalId) || null;

		closeModal(modal);
	});

	document.addEventListener('lecardo-vein:modal:swap-request', (event) => {
		const fromModalId = event.detail?.fromModalId;
		const toModalId = event.detail?.toModalId;
		const fromModal = modalById.get(fromModalId) || null;
		const toModal = modalById.get(toModalId) || null;

		swapModal(fromModal, toModal);
	});

	modals.forEach((modal) => {
		modal.querySelectorAll('[data-modal-close]').forEach((closeEl) => {
			closeEl.addEventListener('click', () => {
				closeModal(modal);
			});
		});
	});

	document.addEventListener('keydown', (event) => {
		if ('Escape' !== event.key) {
			return;
		}

		const openModalEl = document.querySelector('[data-modal].is-open');

		if (openModalEl) {
			closeModal(openModalEl);
		}
	});
}
